import React from 'react';
import { Drawer, Spin } from 'antd';
import { CloseIcon, ModalCallIcon, ModalHeaderBar } from 'assets';
import { Typography } from 'components/common';
import { CallHistoryRequest, useCallHistoryQuery } from 'services/callHistory.service';
import { useAppSelector } from '../../../../redux/hooks';
import { RootState } from '../../../../redux/store';
import CallHistoryCard from './CallHistoryCard';
import IntegrationCard from './IntegrationCard';
import { DRAWER_CONFIG } from './constants';
import './infoDrawer.scss';

interface InfoDrawerProps {
  open: boolean;
  onCancel: () => void;
  thirdPartyIntegrations?: any[];
  isFetching?: boolean;
  callHistoryRequest?: CallHistoryRequest;
}

const InfoDrawer: React.FC<InfoDrawerProps> = ({
  open,
  onCancel,
  thirdPartyIntegrations = [],
  isFetching = false,
  callHistoryRequest
}) => {
  const extensions = useAppSelector((state: RootState) => state.apps.data.extension);
  const { data: callHistoryData, isLoading: isLoadingCallHistory, error: callHistoryError } = useCallHistoryQuery(
    callHistoryRequest ?? null,
    open
  );

  return (
    <Drawer
      open={open}
      onClose={onCancel}
      placement={DRAWER_CONFIG.placement}
      closable={false}
      width={DRAWER_CONFIG.width}
      className="info-drawer-container"
      mask={true}
      maskClosable={true}
    >
      <div className="drawer-close-icon" onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onCancel();
      }}>
        <img src={CloseIcon} alt="close-icon" />
      </div>

      <div className="drawer-info-container">
        <img src={ModalHeaderBar} alt="drawer-header-bar" className="drawer-header-bar-img" />
        <div className="drawer-call-icon-img">
          <img src={ModalCallIcon} alt="drawer-call-icon" />
        </div>
        <Typography variant="subText" className="main-heading">
          Call Details
        </Typography>

        <p className="caller-id">
          Caller ID Number: {callHistoryRequest?.callerIDNumber}
        </p>

        <div className="drawer-scroll-wrapper">
          <div className="caller-info-container">
            <Typography variant="subText" className="section-heading">
              Third Party Integrations
            </Typography>
            
            {isFetching ? (
              <div style={{ textAlign: 'center', padding: '20px' }}>
                <Typography variant="body-text-3">Loading integrations...</Typography>
              </div>
            ) : thirdPartyIntegrations.length > 0 ? (
              thirdPartyIntegrations.map((integration: any, idx: number) => (
                <IntegrationCard 
                  key={idx} 
                  integration={integration} 
                  index={idx}
                />
              ))
            ) : (
              null
            )}
          </div>

          <div className="caller-info-container">
            <Typography variant="subText" className="section-heading">
              Recent Call History
            </Typography>
            
            {isLoadingCallHistory ? (
              <div style={{ textAlign: 'center', padding: '20px' }}>
                <Spin size="small" />
                <Typography variant="body-text-3" style={{ marginTop: '8px' }}>
                  Loading call history...
                </Typography>
              </div>
            ) : callHistoryError ? (
              <div style={{ textAlign: 'center', padding: '20px' }}>
                <Typography variant="body-text-3" style={{ color: '#ff6b6b' }}>
                  {callHistoryError?.message || 'Failed to load call history'}
                </Typography>
              </div>
            ) : callHistoryData?.records && callHistoryData.records.length > 0 ? (
              callHistoryData.records.map((record: any, idx: number) => (
                <CallHistoryCard key={idx} record={record} extensions={extensions} index={idx} />
              ))
            ) : (
              <div style={{ textAlign: 'center', padding: '20px' }}>
                <Typography variant="body-text-3">
                  No call history found
                </Typography>
              </div>
            )}
          </div>
        </div>
      </div>
    </Drawer>
  );
};

export default InfoDrawer;
